import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MarktArticleSelector from '../../../components/article/MarktArticleSelector';
import { useCreateMarktStockMovementMutation } from '../../../redux/api/marktApi';
import { formatApiError } from '../../../hooks/useApiError';

const MarktGoodsReceipt = () => {
  const [articlesToBook, setArticlesToBook] = useState([]);
  const [doCreateStockMovement, { isLoading }] =
    useCreateMarktStockMovementMutation();
  const navigate = useNavigate();

  const handleSetArticlesToBook = useCallback((newArticles) => {
    setArticlesToBook(newArticles);
  }, []);

  const removeLine = (indexToRemove) => {
    setArticlesToBook((prevArticles) =>
      prevArticles.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleChangeQuantity = (event, index) => {
    const newQuantity = parseInt(event.target.value) || 0;

    setArticlesToBook((prevArticles) => {
      const updatedArticles = [...prevArticles];
      updatedArticles[index].quantity = newQuantity;
      return updatedArticles;
    });
  };

  const handleSubmit = async () => {
    if (articlesToBook.length === 0) {
      toast.info('Bitte Artikel zum Buchen hinzufügen.');
      return;
    }

    try {
      // Use Promise.allSettled to ensure all mutations are attempted
      // regardless of individual success/failure, and to await all of them.
      const results = await Promise.allSettled(
        articlesToBook.map((item) => {
          const payload = {
            article_id: item.article.id,
            direction: 'IN', // Fixed to "IN" as per goods receipt
            quantity: item.quantity,
          };
          return doCreateStockMovement(payload).unwrap(); // .unwrap() throws if the mutation fails
        })
      );

      const successfulBookings = results.filter(
        (result) => result.status === 'fulfilled'
      );
      const failedBookings = results.filter(
        (result) => result.status === 'rejected'
      );

      if (successfulBookings.length === articlesToBook.length) {
        toast.success('Alle Artikel erfolgreich eingebucht!');
        setArticlesToBook([]);
        navigate('/markt/stock');
      } else if (failedBookings.length > 0) {
        const detail = failedBookings
          .map((f, i) => `#${i + 1}: ${formatApiError(f.reason)}`)
          .join('\n');
        toast.error(
          `Fehler beim Einbuchen von ${failedBookings.length} Artikel(n).\n${detail}`
        );
        failedBookings.forEach((failure, index) => {
          console.error(
            `Buchungsfehler für Artikel ${index + 1}:`,
            failure.reason
          );
        });
      }
    } catch (error) {
      toast.error(formatApiError(error) || 'Ein unerwarteter Fehler ist aufgetreten.');
    }
  };

  return (
    <div>
      <h3>Getränkemarkt - Wareneingang buchen</h3>
      <div className='row mt-5'>
        <div className='col-3'>
          <label htmlFor='article' className='col-form-label'>
            Artikel hinzufügen
          </label>
        </div>
        <div className='col-3'>
          <MarktArticleSelector
            articlesToBook={articlesToBook}
            setArticlesToBook={handleSetArticlesToBook}
          />
        </div>
      </div>
      <div className='mt-5'></div>
      {articlesToBook.length > 0 ? (
        <>
          <h4>Einzubuchende Artikel:</h4>
          <table className='table table-striped mt-3 align-middle'>
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>Artikel</th>
                <th>Gebinde</th>
                <th>Menge</th>
                <th>in Paletten</th>
              </tr>
            </thead>
            <tbody>
              {articlesToBook?.map((artLine, index) => (
                <tr key={index}>
                  <td>
                    <button
                      className='btn btn-sm ps-0 text-danger border-0'
                      onClick={() => removeLine(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                  <td>{artLine.article.name}</td>
                  <td>{artLine.article.unit}</td>
                  <td>
                    <input
                      className='form-control'
                      type='number'
                      value={artLine.quantity}
                      onChange={(event) => handleChangeQuantity(event, index)}
                    />
                  </td>
                  <td>
                    {artLine.article.pallet_factor > 0
                      ? (() => {
                          const fullPallets = Math.floor(
                            artLine.quantity / artLine.article.pallet_factor
                          );
                          const remainder =
                            artLine.quantity % artLine.article.pallet_factor;
                          const unitName = artLine.article.unit;
                          return remainder === 0
                            ? `${fullPallets} PAL`
                            : fullPallets > 0
                              ? `${fullPallets} PAL + ${remainder} x ${unitName}`
                              : `${remainder} x ${unitName}`;
                        })()
                      : 'nicht palletiert'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            className='btn btn-success'
            onClick={handleSubmit}
            disabled={isLoading}
          >
            Wareneingang buchen
          </button>
        </>
      ) : null}
    </div>
  );
};

export default MarktGoodsReceipt;
